import React from 'react';
import { User as UserIcon } from 'lucide-react';
import { Button, Dropdown } from 'antd';
import { Layout } from 'antd';
import { AppMenu } from './AppMenu';
import { UserStoreProvider } from '@/hooks/UserStoreContext';
import { User } from '@/types/user';
import { useLogoutMutation, userQuery } from '@/queries/user.query';
import { useNavigate } from 'react-router';
import { useApiErrorHandler } from '@/libs/api-error-handler';
import { ApiError } from '@/queries/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { clearUserInfo } from '@/utils/user';

const { Sider } = Layout;

const AuthSide: React.FC<{ user?: User }> = ({ user }) => (
  <Sider>
    <div className="h-screen flex flex-col sticky top-0 border-r bg-[#121212] w-[200px] box-content">
      <div className="pl-8 pt-8 pb-7">
        <h1 className="text-xl font-bold mb-1 text-white">ArcSite</h1>
        <p className="text-[#888] text-[12px]">Enterprise Admin Console</p>
      </div>
      {user ? (
        <>
          <div className="!flex-grow">
            <AppMenu user={user} />
          </div>
          <UserProfile user={user} />
        </>
      ) : null}
    </div>
  </Sider>
);

const UserProfile: React.FC<{ user: User }> = ({ user }) => {
  const navigate = useNavigate();
  const { handleApiError } = useApiErrorHandler();
  const queryClient = useQueryClient();
  const { mutate: logout, reset } = useLogoutMutation({
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: userQuery.queryKey });
      navigate('/login');
    },
    onError: error => {
      handleApiError(error as ApiError, {
        title: 'Logout failed',
        onRetry: reset,
      });
    },
  });
  const menuItems = [
    {
      key: 'logout',
      label: 'Logout',
      onClick: () => {
        clearUserInfo();
        logout();
      },
    },
  ];
  return (
    <UserStoreProvider initialUser={user}>
      <div className="h-40">
        <Button type="text" className="flex items-center gap-2">
          <UserIcon className="w-4 h-4 text-white flex-shrink-0" />
          <Dropdown menu={{ items: menuItems }} placement="bottomRight">
            <div className="flex flex-col text-white w-[140px]">
              <span className="text-left block truncate">{user.fullname}</span>
              <span className="text-left text-[12px] block truncate">
                {user.username}
              </span>
            </div>
          </Dropdown>
        </Button>
      </div>
    </UserStoreProvider>
  );
};

export default AuthSide;
